import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { WorkFunction } from '../shared/model/work-function/work-function.model';
import { FacadeService } from '../shared/service/facade/facade.service';
import { AuthorizationService } from '../login/shared/service/authorization.service';
import { EventResizeDoneArg } from '@fullcalendar/interaction';
import { MatDialog } from '@angular/material/dialog';
import { EventDialogComponent } from './event-dialog/event-dialog.component';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Period, Request } from './shared/model/request.model';
import { Location } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Worker } from '../shared/model/worker/worker.model';
import { CalendarOptions, DateSelectArg, EventClickArg } from '@fullcalendar/core';
import { AppComponent } from '../app.component';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { NonAvailability } from 'src/app/shared/model/non-availability/nonAvailability.model';
import { Prestation } from '../prestations/shared/model/prestation.model';
import { PrestationPeriod } from '../prestations/shared/model/prestation-period.model';
import { Observable } from 'rxjs/internal/Observable';
import { map, startWith } from 'rxjs/operators';


@Component({
  selector: 'app-request',
  templateUrl: './request.component.html',
  styleUrls: ['./request.component.scss']
})
export class RequestComponent implements OnInit, AfterViewInit {
  private readonly EVENT_MINIMUM_DURATION = 10800000; //  3 hours
  private readonly EVENT_MAXIMUM_DURATION = 39600000; //  11  hours
  costCenterList: string[] = [];
  workFunctionList: WorkFunction[] = [];
  workerList: Worker[] = [];
  customerId: number;
  form: UntypedFormGroup;
  @Input() request: Request;


  @ViewChild('calendar') calendarRef: FullCalendarComponent;
  requestCalendarOptions: CalendarOptions = {
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'timeGridWeek,timeGridDay,listWeek'
    },
    initialView: 'timeGridWeek',
    weekends: true,
    editable: true,
    selectable: true,
    selectMirror: false,
    dayMaxEvents: true,
    allDaySlot: false,
    nowIndicator: true,
    locale: this.translate.currentLang,
    select: this.handleDateSelect.bind(this),
    eventResize: this.handleResize.bind(this),
    eventClick: this.handleEventClick.bind(this),
  };
  calendarOptions: CalendarOptions;
  workerOptions: Observable<Worker[]>;
  modifiable = true;
  startDate: Date;
  endDate: Date;
  requests: any[] = [];
  nonAvailabilities: any[] = [];
  private readonly NON_AVAILABILITIES_SOURCE_ID = 'non-availabilities';
  private readonly REQUEST_SOURCE_ID = 'requests';

  constructor(private facadeService: FacadeService,
    private authorizationService: AuthorizationService,
    private formBuilder: UntypedFormBuilder,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private location: Location) {

    this.request = this.route.snapshot.data['request'];
    this.costCenterList = this.route.snapshot.data['customer'].costCenter;
    this.customerId = this.route.snapshot.data['customer'].id;

    if (!this.request) {
      this.request = new Request();
    }

    this.modifiable = !this.request.id || this.request.isModifiable;

    this.calendarOptions = {
      ...AppComponent.calendarOptions,
      ...this.requestCalendarOptions,
      selectable: this.modifiable,
      editable: this.modifiable,
      validRange: {
        start: this.modifiable ? new Date() : null
      },
    };
  }

  ngOnInit() {
    this.buildForm();
    this.getWorkFunctions();
    this.getWorkers();
  }


  ngAfterViewInit() {

    if (this.request) {

      this.request.periods.forEach(
        period => {
          const event = {
            id: period.id.toString(),
            start: period.startDate,
            end: period.endDate
          };
          if (period.description) {
            event['title'] = period.description;
            event['description'] = period.description;
          }
          this.calendarRef.getApi().addEvent(event);
        }
      );

      this.calendarRef.getApi().gotoDate(this.request.startDate);
    } else {
        this.startDate = this.calendarRef.getApi().view.activeStart;
        this.endDate = this.calendarRef.getApi().view.activeEnd;
        this.getNonAvailabilitiesForWorker();
      }


  }

  onSelected() {
    this.getNonAvailabilitiesForWorker();
    this.getRequestForWorker();
  }

  submit() {
    if (!this.modifiable) {
      return this.router.navigate(['planning']);
    }

    if (!this.checkRequestValidity()) {
      return this.openSnackBar(this.translate.instant('request.message.error.overlap'), 'customSnackError');
    }

    let periods = this.form.controls.periods as UntypedFormArray;
    let events = this.calendarRef.getApi().getEvents();
    events.forEach(
      event => {
        periods.push(this.buildPeriodForm(event.id, event.start, event.end, event.title));
      }
    );

    if (!this.form.valid) {
      periods.clear();
      return this.openSnackBar(this.translate.instant('request.message.error.form'));
    }

    const min = new Date(events.reduce((a, p) => p.start.getTime() < a ? p.start.getTime() : a, events[0].start.getTime()));
    const max = new Date(events.reduce((a, p) => p.end.getTime() > a ? p.end.getTime() : a, events[0].end.getTime()));

    let request = this.form.value as Request;
    request = this.cleanExistingOnes(request);
    request.startDate = min;
    request.endDate = max;

    request.worker = new Worker();
    request.workFunctionId = this.form.controls.workFunctionId?.value;

    request.worker.id = this.form.controls.workerId.value;

    this.facadeService.saveRequest(request).subscribe({
      next: ignore => {
        this.router.navigate(['planning']).then();
        this.openSnackBar(this.translate.instant('request.message.success.save'), 'customSnackValid');
      },
      error: ignore => {
        this.openSnackBar(this.translate.instant('request.message.error.save'));
      }
    });
  }

  private cleanExistingOnes(request: Request) {
    let finalPeriods : Period[] = new Array();

    for (const period of request.periods) {

      if (period.id !== null) {
        period.id = null;
        finalPeriods.push(period);
      }
    }

    request.periods = finalPeriods;
    return request;
  }

  handleEventClick(clickInfo: EventClickArg) {
    if (this.modifiable) {
      this.openEventDialog(clickInfo.event.start, clickInfo.event.end, clickInfo.event.extendedProps.description, clickInfo.event.id);
    }
  }

  handleResize(eventResizeInfo: EventResizeDoneArg) {
    const start = new Date(eventResizeInfo.event.start);
    const end = new Date(eventResizeInfo.event.end);

    const answer = this.checkEventValidity(start, end);

    if (!answer && this.modifiable) {
      eventResizeInfo.revert();
    }
  }

  handleDateSelect(selectInfo: DateSelectArg) {
    const start = new Date(selectInfo.startStr);
    const end = new Date(selectInfo.endStr);

    const answer = this.checkEventValidity(start, end);

    if (answer && this.modifiable) {
      this.openEventDialog(selectInfo.start, selectInfo.end);
    }
  }

  private openEventDialog(start: Date, end: Date, description: string = null, eventId: any = null) {
    const dialogRef = this.dialog.open(EventDialogComponent, {
      data: {
        description: description,
        eventId: eventId
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      //  Result[0]: Description
      //  Result[1]: Is a delete ?
      const calendarApi = this.calendarRef.getApi().view.calendar;
      calendarApi.unselect(); // clear date selection
      if (result && result[1]) {
        return this.deleteEvent(eventId);
      }
      if (result) {
        if (eventId) {
          this.deleteEvent(eventId);
        }

        this.checkEventValidity(start, end);

        const event = {
          id: Math.random().toString(36).slice(2, 9),
          start: start,
          end: end
        };
        if (result[0]) {
          event['title'] = result[0];
          event['description'] = result[0];
        }
        calendarApi.addEvent(event);
      }

    });
  }

  addNonAvailabilities() {
    this.calendarRef.getApi().addEventSource({
      id: this.NON_AVAILABILITIES_SOURCE_ID,
      events: this.nonAvailabilities
    });
  }

  addWorkerRequest() {
    this.calendarRef.getApi().addEventSource({
      id: this.REQUEST_SOURCE_ID,
      events: this.requests
    });
  }

  private getNonAvailabilitiesForWorker() {
    if (this.calendarRef) {
      const workerId = this.form.controls['workerId'].value;
      if (workerId) {
        const startDate = this.calendarRef.getApi().view.activeStart;
        const endDate = this.calendarRef.getApi().view.activeEnd;
        this.facadeService.getNonAvailabilitiesFilter(workerId, startDate, endDate)
          .subscribe({
            next: (nonAvailabilities: NonAvailability[]) => {
              this.nonAvailabilities = [];
              const sources = this.calendarRef.getApi().getEventSourceById(this.NON_AVAILABILITIES_SOURCE_ID);
              if (sources) {
                sources.remove();
              }
              nonAvailabilities.forEach(
                nonAvailability => {
                  const event = NonAvailability.buildEvent(nonAvailability);
                  event['editable'] = false;
                  event['overlap'] = false;
                  this.nonAvailabilities.push(event);
                }
              );
              this.addNonAvailabilities();
            },
            error: () => {
              return this.openSnackBar(this.translate.instant('requests.form.message.error.nonAvailabilities'), 'customSnackError');
            }
          });
      }
    }
  }

  private getRequestForWorker() {
    if (this.calendarRef) {
      const workerId = this.form.controls['workerId'].value;
      if (workerId) {
        const startDate = this.calendarRef.getApi().view.activeStart;
        const endDate = this.calendarRef.getApi().view.activeEnd;
        this.facadeService.filterPrestation(startDate, endDate, workerId, this.customerId).subscribe({
          next: (prestations: Prestation[]) => {
            this.requests = [];
            if (!this.calendarRef.getApi()) {
              return;
            }
            const sources = this.calendarRef.getApi().getEventSourceById(this.REQUEST_SOURCE_ID);
            if (sources) {
              sources.remove();
            }
            prestations.forEach(
              prestation => {
                prestation.periods.forEach(
                  prestationPeriod => {
                    if (prestation.requestId !== this.request.id) {
                      this.requests.push(RequestComponent.buildRequestsEvent(prestationPeriod, prestation));
                    }
                  }
                );
              }
            );
            this.addWorkerRequest();
          },
          error: () => {
            return this.openSnackBar(this.translate.instant('requests.form.message.error.nonAvailabilities'), 'customSnackError');
          }
        });
      }
    }
  }

  private static buildRequestsEvent(prestationPeriod: PrestationPeriod, prestation: Prestation) {
    const start = prestationPeriod.plannedStartDate;
    const end = prestationPeriod.plannedEndDate;
    return {
      title: prestation.customerName,
      start: start,
      end: end,
      editable: false,
      overlap: false,
      color: 'blue',
      prestationPeriod: prestationPeriod
    };
  }

  private deleteEvent(eventId: any) {
    const event = this.calendarRef.getApi().getEventById(eventId);
    if (event) {
      event.remove();
    }
  }


  deleteRequest(request: Request) {

    // Check if request's first period's start date is < new Date() so that it can still be deleted //
    const sortedPeriods = request.periods.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
    const firstStartDate = sortedPeriods[0].startDate;

    if (firstStartDate < new Date()) {
      return;
    } else {
      this.facadeService.deleteRequest(request).subscribe({
        next: () => {
          this.openSnackBar(this.translate.instant('request.message.success.delete'), 'customSnackValid');
          this.returnToPlanning();
        },
        error: () => {
          return this.openSnackBar(this.translate.instant('request.message.error.delete'), 'customSnackError');
        }
      });
    }
  }

  private checkEventValidity(start: Date, end: Date) {
    const diff = end.getTime() - start.getTime();

    if (start.getTime() < Date.now()) {
      this.openSnackBar(this.translate.instant('request.message.error.checkEventValidity.past'));
      return false;
    }

    if (diff < this.EVENT_MINIMUM_DURATION) {
      this.openSnackBar(this.translate.instant('request.message.error.checkEventValidity.minimum'));
      return false;
    }

    if (diff > this.EVENT_MAXIMUM_DURATION) {
      this.openSnackBar(this.translate.instant('request.message.error.checkEventValidity.maximum'));
      return false;
    }

    return true;
  }

  private checkRequestValidity() {
    let isValid = true;
    const events = this.calendarRef.getApi().getEvents();
    events.sort((a, b) => a.start.getTime() - b.start.getTime());
    for (let i = 0; i < events.length - 1; i++) {
      const eventSourceI = events[i].source;
      const eventSourceIAfter = events[i + 1].source;
      if (!([this.NON_AVAILABILITIES_SOURCE_ID, this.REQUEST_SOURCE_ID].includes(eventSourceI?.id)
        && ([this.NON_AVAILABILITIES_SOURCE_ID, this.REQUEST_SOURCE_ID].includes(eventSourceIAfter?.id)))) {
        if (RequestComponent.checkOverlap(events[i].start, events[i].end, events[i + 1].start, events[i + 1].end)) {
          return isValid = false;
        }
      }
    }
    return isValid;
  }

  private static checkOverlap(start_a: Date, end_a: Date, start_b: Date, end_b: Date): boolean {
    return (start_a.getTime() < end_b.getTime() && start_b.getTime() < end_a.getTime());
  }

  private buildForm() {
    this.form = this.formBuilder.group({
      id: [this.request.id, []],
      costCenter: [{ value: this.request.costCenter, disabled: !this.modifiable }, []],
      workFunctionId: [{ value: this.request.workFunction?.id, disabled: !this.modifiable }, [Validators.required]],
      workerId: [{ value: this.request.worker ? this.request.worker.id : null, disabled: !this.modifiable }, []],
      periods: this.formBuilder.array([], [Validators.required]),
    });
  }

  private buildPeriodForm(id: string, start: Date, end: Date, title: string) {
    return this.formBuilder.group({
      id: [(id ? id : null), []],
      description: [(title ? title : null), []],
      startDate: [start, [Validators.required]],
      endDate: [end, [Validators.required]],
    });
  }

  private getWorkFunctions() {
    this.facadeService.listWorkFunctions().subscribe({
      next: (workFunctions: WorkFunction[]) => {
        this.workFunctionList = workFunctions;

        this.workFunctionList = this.workFunctionList.sort(function (a, b) {
          return a.nameTranslate.localeCompare(b.nameTranslate);
        }
        )
      },
      error: ignore => {
        this.openSnackBar(this.translate.instant('request.message.error.workFunctions'));
      }
    });
  }

  private getWorkers() {
    this.facadeService.getPastWorkers().subscribe({
      next: (worker: Worker[]) => {
        this.workerList = worker;
        this.workerOptions = this.form.controls['workerId'].valueChanges
        .pipe(
          startWith(''),
          map(value => this._filterWorker(value))
        );
      },
      error: ignore => {
        this.openSnackBar(this.translate.instant('request.message.error.workFunctions'));
      }
    });
  }

  public buildWorkerName(worker: Worker): string {
    return worker.id + ' ' + worker.lastName.trim().toLowerCase() + ' ' + worker.firstName.trim().toLowerCase();
  }

  getWorkerName(value) {
    const results = this.workerList.filter(w => w.id === +value);
    if (results.length) {
      return '#' + results[0].id + ' - ' + results[0].lastName.trim() + ' ' + results[0].firstName.trim();
    }
    return value;
  }

  private _filterWorker(value: any): Worker[] {
    if (typeof value !== 'string') {
      return [];
    }
    const filterValue = value.toLowerCase();
    return this.workerList.filter(option => this.buildWorkerName(option).includes(filterValue));
  }

  private openSnackBar(msg: string, pC: string = 'customSnackError') {
    this.snackBar.open(msg, 'X', {
      duration: 5000,
      panelClass: [pC]
    });
  }

  returnToPlanning() {
    return this.router.navigate(['planning']);
  }
}
